import { useNavigate } from "react-router-dom";
import { APP_NAME, BrandMark } from "../../config/brand";
import { useAuth } from "../../providers/AuthProvider";
import { useLogout } from "../../hooks/useLogout";
import { Breadcrumb } from "../breadcrumb/Breadcrumb";
import { Button } from "../ui/Button";
import { UploadButton } from "../upload/UploadButton";
import type { Crumb } from "../../lib/api/types";

interface AppHeaderProps {
  folderId: string | null;
  crumbs: Crumb[];
  onNewFolder: () => void;
}

export function AppHeader({ folderId, crumbs, onNewFolder }: AppHeaderProps) {
  const navigate = useNavigate();
  const { user, config } = useAuth();
  const logout = useLogout();
  const appName = config?.appName ?? APP_NAME;
  const isAdmin = user?.role === "admin";
  const displayName = user?.displayName ?? user?.username ?? "Account";

  const onCrumbNavigate = (id: string) => {
    if (id === "root") {
      navigate("/");
    } else {
      navigate(`/folder/${id}`);
    }
  };

  return (
    <header className="border-b border-surface-border bg-surface">
      <div className="mx-auto flex max-w-5xl flex-col gap-3 px-gutter py-3 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="flex shrink-0 items-center gap-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            aria-label={`${appName} — Home`}
          >
            <BrandMark className="h-6 w-6 text-brand-600" />
            <span className="hidden text-brand font-semibold text-text sm:inline">{appName}</span>
          </button>
          <span className="hidden h-6 w-px shrink-0 bg-surface-border md:inline-block" aria-hidden="true" />
          <Breadcrumb crumbs={crumbs} onNavigate={onCrumbNavigate} />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* File-management controls are admin-only; investors are read-only (PRD §4). */}
          {isAdmin ? (
            <>
              <Button variant="secondary" onClick={onNewFolder}>
                <span aria-hidden="true">+</span>
                <span>New folder</span>
              </Button>
              <UploadButton folderId={folderId} />
            </>
          ) : null}
          {/* Compact user chip + logout for mobile, where the sidebar is hidden. */}
          <span className="text-body font-medium text-text md:hidden" title={displayName}>
            {displayName}
          </span>
          <Button variant="ghost" className="md:hidden" onClick={() => void logout()}>
            Logout
          </Button>
        </div>
      </div>
    </header>
  );
}
